import React, { useMemo, useId } from 'react';
import PropTypes from 'prop-types';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';

/**
 * LatencyChart - Displays latency over time as an area chart
 * Shows response times trend for monitoring performance
 */
const LatencyChart = ({ runs, loading }) => {
  // Unique ID for gradient to avoid conflicts with multiple instances
  const gradientId = useId();

  // Transform runs data for the chart - reverse to show oldest first
  const { chartData, stats } = useMemo(() => {
    if (!runs || runs.length === 0) return { chartData: [], stats: null };
    
    const filtered = runs
      .filter(run => run.latency_ms && run.created_at)
      .map(run => ({
        timestamp: new Date(run.created_at).getTime(),
        latency: run.latency_ms,
        latencySeconds: (run.latency_ms / 1000).toFixed(2),
        status: run.status,
        model: run.model,
        name: run.project_name || 'Untitled'
      }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-30);
    
    // Calculate stats
    if (filtered.length > 0) {
      const latencies = filtered.map(d => d.latency);
      const avg = latencies.reduce((a, b) => a + b, 0) / latencies.length;
      const min = Math.min(...latencies);
      const max = Math.max(...latencies);
      
      return {
        chartData: filtered,
        stats: { avg, min, max }
      };
    }
    
    return { chartData: filtered, stats: null };
  }, [runs]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="rounded-lg p-3 shadow-lg bg-bg-secondary/95 border border-primary-500/30 backdrop-blur-sm">
          <p className="text-xs mb-1 text-text-tertiary">
            {format(new Date(data.timestamp), 'MMM d, h:mm a')}
          </p>
          <p className="text-sm font-semibold text-primary-500">
            {data.latencySeconds}s
          </p>
          <p className="text-xs mt-1 text-text-secondary">
            {data.name}
          </p>
          <p className="text-xs text-text-tertiary">
            {data.model}
          </p>
        </div>
      );
    }
    return null;
  };
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array
  };

  // Format X-axis tick
  const formatXAxis = (timestamp) => {
    return format(new Date(timestamp), 'h:mm a');
  };

  // Format Y-axis tick (convert ms to seconds)
  const formatYAxis = (value) => {
    return `${(value / 1000).toFixed(1)}s`;
  };

  if (loading && (!runs || runs.length === 0)) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary">
        <div className="h-4 w-32 rounded mb-4 animate-pulse bg-success-50"></div>
        <div className="h-[200px] rounded animate-pulse bg-primary-500/5"></div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
        <h3 className="text-sm font-semibold mb-4 text-text-primary">
          Latency Over Time
        </h3>
        <div className="h-[200px] flex flex-col items-center justify-center">
          <svg className="w-10 h-10 mb-2 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-sm text-text-tertiary">No latency data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-6 bg-bg-secondary/60 border border-border-secondary transition-all duration-200 hover:border-primary-500/25">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-text-primary">
            Latency Over Time
          </h3>
          {stats && (
            <p className="text-xs mt-0.5 text-text-tertiary">
              Avg {(stats.avg / 1000).toFixed(1)}s · Min {(stats.min / 1000).toFixed(1)}s · Max {(stats.max / 1000).toFixed(1)}s
            </p>
          )}
        </div>
        <span className="text-xs text-text-tertiary">
          Last {chartData.length} runs
        </span>
      </div>
      
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={`latencyGradient-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#32CD32" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#32CD32" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="rgba(50, 205, 50, 0.1)" 
              vertical={false}
            />
            <XAxis 
              dataKey="timestamp"
              tickFormatter={formatXAxis}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
            />
            <YAxis 
              tickFormatter={formatYAxis}
              stroke="#6B8E6B"
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
              tick={{ fill: '#6B8E6B' }}
              width={45}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="latency"
              stroke="#32CD32"
              strokeWidth={2}
              fill={`url(#latencyGradient-${gradientId})`}
              dot={false}
              activeDot={{ 
                r: 4, 
                fill: '#32CD32', 
                stroke: '#111', 
                strokeWidth: 2 
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

LatencyChart.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.shape({
    latency_ms: PropTypes.number,
    created_at: PropTypes.string,
    run_name: PropTypes.string,
    llm_config: PropTypes.object
  })),
  loading: PropTypes.bool
};

export default LatencyChart;
